/**
 * Centralized site URL utility for auth redirects
 *
 * SECURITY: This utility provides a secure way to get the site URL for
 * OAuth redirects and email verification links. Using window.location.origin
 * directly is insecure because:
 *
 * 1. It can be manipulated in iframe contexts
 * 2. Attackers could potentially redirect users to phishing sites after OAuth
 * 3. DOM clobbering attacks could override window.location
 *
 * The NEXT_PUBLIC_SITE_URL environment variable should ALWAYS be set in
 * production to ensure redirect URLs are trustworthy.
 */

/**
 * Get the site URL for auth redirects.
 *
 * Priority:
 * 1. NEXT_PUBLIC_SITE_URL env var (most secure, should always be set in production)
 * 2. window.location.origin (fallback for development)
 * 3. Empty string (SSR fallback, should not happen in practice)
 *
 * @returns The site URL without trailing slash
 */
export function getSiteUrl(): string {
  // Server-side or runtime env var available - preferred for security
  const runtimeSiteUrl =
    typeof window !== 'undefined'
      ? window.__RUNTIME_CONFIG__?.NEXT_PUBLIC_SITE_URL
      : undefined;
  const envSiteUrl = process.env.NEXT_PUBLIC_SITE_URL;
  const resolvedSiteUrl = runtimeSiteUrl || envSiteUrl;
  if (resolvedSiteUrl) {
    // Remove trailing slash if present for consistency
    return resolvedSiteUrl.replace(/\/$/, '');
  }

  // Client-side fallback - only for development
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  // SSR fallback (should not happen in practice for auth flows)
  return '';
}

/**
 * Build an auth callback URL with optional parameters
 *
 * @param path - The callback path (e.g., '/auth/callback')
 * @param params - Optional URL parameters to append
 * @returns Full callback URL
 */
export function buildAuthCallbackUrl(
  path: string = '/auth/callback',
  params?: Record<string, string>
): string {
  const baseUrl = getSiteUrl();
  const url = new URL(path, baseUrl);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
  }

  return url.toString();
}
